import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../providers/authprovider";
import handleResponseCode from "./utils/handleResponseCode";
import { Alert, Button, Spinner, Table, Badge, Modal } from "react-bootstrap";
import useInvoiceModal from "./hooks/useInvoiceModal";
import { faTrash, faEdit } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export default function InvoicesList({ buildings = [], onInvoiceAdded, onInvoiceDeleted }) {
  const { buildingId } = useParams();
  const navigate = useNavigate();
  const { invoices: invoicesApi } = useAuth();
  const { createInvoiceModal, CreateInvoiceModal } = useInvoiceModal();

  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [invoices, setInvoices] = useState([]);

  // confirm modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // invoice

  const buildingIdNum = useMemo(() => buildingId, [buildingId]);

  const building = useMemo(() => {
    return buildings
      .map((b) => ({ ...b, id: b.buildingId }))
      .find((b) => b.id === buildingIdNum || String(b.id) === String(buildingId));
  }, [buildings, buildingIdNum, buildingId]);

  const handleDownload = useCallback(
    async (invoice) => {
      if (!invoice?.id) return;

      const result = await invoicesApi.downloadInvoicePdf({
        invoiceId: invoice.id,
        fileName: invoice.file_name,
      });

      if (!result?.success) {
        console.error("Błąd pobierania faktury:", result?.error || "Nieznany błąd");
      }
    },
    [invoicesApi],
  );

  const loadInvoices = useCallback(async () => {
    if (!buildingId) return;

    setLoading(true);
    setError("");

    const result = await invoicesApi.fetchBuildingInvoices(buildingId);

    if (!result?.success) {
      setInvoices([]);
      setError(handleResponseCode(result?.status, result?.error));
      setLoading(false);
      return;
    }

    setInvoices(result.content || []);
    setLoading(false);
  }, [invoicesApi, buildingId]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const formatDateTime = (iso) => {
    if (!iso) return "";

    return new Intl.DateTimeFormat("pl-PL", {
      timeZone: "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(iso));
  };

  const handleSave = (invoice) => {
    let isNew = false;

    setInvoices((oldInvoices) => {
      const existingIndex = oldInvoices.findIndex((i) => i.id === invoice.id);

      if (existingIndex > -1) {
        return oldInvoices.map((i) => (i.id === invoice.id ? { ...i, ...invoice } : i));
      }

      isNew = true;
      return [...oldInvoices, invoice];
    });

    if (isNew) {
      onInvoiceAdded?.(invoice, building);
    }
  };

  const openDeleteModal = (inv) => {
    setError("");
    setDeleteTarget(inv);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;

    setDeleting(true);
    setError("");

    const result = await invoicesApi.deleteInvoice(deleteTarget.id);

    if (!result?.success) {
      setError(handleResponseCode(result?.status, result?.error));
      setDeleting(false);
      return;
    }

    setInvoices((old) => old.filter((i) => i.id !== deleteTarget.id));
    setDeleting(false);
    closeDeleteModal();
    onInvoiceDeleted?.(deleteTarget, building);
  };

  if (!building && !loading) {
    return <div>Building not found</div>;
  }

  return (
    <div className="container py-3">
      {!loading ? (
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h4 className="mb-1">
              Faktury <Badge bg="secondary">{invoices.length}</Badge>
            </h4>
            <div className="text-muted small">
              Obiekt:{" "}
              <strong>
                {building?.name || building?.building_name || building?.title || buildingId}
              </strong>
            </div>
          </div>

          <div>
            <Button
              className="btn btn-primary me-2"
              onClick={() => {
                createInvoiceModal.show({
                  title: "Nowa faktura",
                  initialBuilding: building,
                  onSaved: handleSave,
                });
              }}
            >
              Nowa faktura
            </Button>
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Wróć
            </Button>
          </div>
        </div>
      ) : null}

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {loading ? (
        <div className="py-3">
          <Spinner size="sm" className="me-2" />
          Ładowanie...
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th style={{ width: "220px" }}>Utworzono</th>
              <th>Opis</th>
              <th style={{ width: "260px" }}>Plik</th>
              <th style={{ width: "150px" }}>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center text-muted py-4">
                  Brak faktur w tym obiekcie.
                </td>
              </tr>
            ) : (
              invoices.map((inv) => (
                <tr key={inv.id}>
                  <td>{formatDateTime(inv.created_at)}</td>
                  <td style={{ whiteSpace: "pre-wrap" }}>{inv.description}</td>
                  <td>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleDownload(inv);
                      }}
                    >
                      {inv.file_name}
                    </a>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      onClick={() => {
                        createInvoiceModal.show({
                          title: "Edytuj fakturę",
                          initialBuilding: building,
                          invoice: inv,
                          onSaved: handleSave,
                        });
                      }}
                      title="Edytuj"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>

                    <Button
                      size="sm"
                      variant="danger"
                      className="ms-2"
                      onClick={() => openDeleteModal(inv)}
                      title="Usuń"
                      disabled={deleting && deleteTarget?.id === inv.id}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      <CreateInvoiceModal />

      {/* Confirm delete modal */}
      <Modal show={deleteModalOpen} onHide={closeDeleteModal} centered backdrop="static">
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Usuń fakturę</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          Czy na pewno chcesz usunąć tę fakturę?
          {deleteTarget ? (
            <div className="mt-2 small text-muted">
              <div>
                <strong>Plik:</strong> {deleteTarget.file_name}
              </div>
              <div>
                <strong>Utworzono:</strong> {formatDateTime(deleteTarget.created_at)}
              </div>
            </div>
          ) : null}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteModal} disabled={deleting}>
            Anuluj
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Usuwanie...
              </>
            ) : (
              "Usuń"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
